import { Link } from "@tanstack/react-router";
import { ArrowRight, FileDown } from "lucide-react";

import { ArticleTypeBadge } from "@/components/site/ArticleTypeBadge";
import { ShareLinks } from "@/components/site/ShareLinks";
import { PageHero } from "@/components/site/Blocks";
import type { ArticleSection, ArticleType } from "@/lib/articles.shared";
import { ArticleRichText } from "@/components/site/ArticleRichText";

export interface DbArticleItem {
  slug: string;
  title: string;
  excerpt: string;
  metaTitle: string;
  metaDescription: string;
  topic: string;
  published: string;
  updated: string;
  keywords: string[];
  sections: ArticleSection[];
  faq: Array<[string, string]>;
  cta: { label: string; to: string };
  /** Uploaded PDF path/URL, or null when the PDF is generated on the fly. */
  pdfUrl: string | null;
  /** Public article ID, e.g. `#20260907003_EN`. Null when not assigned. */
  articleIndex?: string | null;
}

export interface DbArticleLabels {
  homeName: string;
  homePath: string;
  sectionName: string;
  sectionPath: string;
  eyebrow: string;
  faqHeading: string;
  nextStepTitle: string;
  nextStepText: string;
  moreHeading: string;
  dateLocale: string;
  downloadLabel: string;
  downloadHint: string;
}

/**
 * Shared renderer for database-backed section articles (technical articles,
 * whitepapers, case studies, trends, FAQ answers) in EN and TR.
 */
export function DbArticleView({
  item,
  related,
  labels,
  type,
  locale,
}: {
  item: DbArticleItem;
  related: Array<{ slug: string; title: string }>;
  labels: DbArticleLabels;
  type: ArticleType;
  locale: "en" | "tr";
}) {
  const hasPdf = type === "whitepaper" || type === "case-study";
  const pdfHref =
    item.pdfUrl ??
    `/api/public/article-pdf?locale=${locale}&slug=${encodeURIComponent(item.slug)}`;

  const trail = [
    { name: labels.homeName, path: labels.homePath },
    { name: labels.sectionName, path: labels.sectionPath },
    { name: item.title, path: `${labels.sectionPath}/${item.slug}` },
  ];

  return (
    <>
      <PageHero eyebrow={labels.eyebrow} title={item.title} intro={item.excerpt} trail={trail} />

      <article className="section-shell py-12 md:py-20">
        <div className="flex flex-wrap items-center gap-3">
          <ArticleTypeBadge type={type} locale={locale} />
        </div>
        <ShareLinks
          className="mt-4"
          path={`${labels.sectionPath}/${item.slug}`}
          title={item.title}
          locale={locale}
        />
        <p className="mt-3 text-sm text-muted-foreground">
          <time dateTime={item.published.slice(0, 10)}>
            {new Date(item.published).toLocaleDateString(labels.dateLocale, { timeZone: "UTC" })}
          </time>
          {item.articleIndex && (
            <>
              <span aria-hidden="true"> · </span>
              {item.articleIndex}
            </>
          )}
        </p>

        {hasPdf && (
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a
              href={pdfHref}
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-2 rounded-md border border-primary bg-primary/10 px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
            >
              <FileDown className="size-4" aria-hidden="true" />
              {labels.downloadLabel}
            </a>
            <span className="text-xs text-muted-foreground">{labels.downloadHint}</span>
          </div>
        )}

        <div className="mt-10 max-w-3xl space-y-12">
          {item.sections.map((section) => (
            <section key={section.heading || section.paragraphs[0]?.slice(0, 40)}>
              {section.heading && (
                <h2 className="text-2xl font-bold md:text-3xl">{section.heading}</h2>
              )}
              {section.paragraphs.map((paragraph) => (
                <p
                  key={paragraph.slice(0, 40)}
                  className="mt-4 text-lg leading-relaxed text-muted-foreground"
                >
                  <ArticleRichText text={paragraph} />
                </p>
              ))}
              {section.bullets && (
                <ul className="mt-5 space-y-3">
                  {section.bullets.map((bullet) => (
                    <li key={bullet.slice(0, 40)} className="flex gap-3 text-muted-foreground">
                      <span
                        aria-hidden="true"
                        className="mt-2 size-1.5 shrink-0 rounded-full bg-primary"
                      />
                      <span><ArticleRichText text={bullet} /></span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {item.faq.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold md:text-3xl">{labels.faqHeading}</h2>
              <dl className="mt-6 space-y-6">
                {item.faq.map(([question, answer]) => (
                  <div key={question}>
                    <dt className="font-semibold">{question}</dt>
                    <dd className="mt-2 text-muted-foreground">{answer}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}
        </div>

        <div className="mt-14 rounded-lg bg-night p-6 text-night-foreground md:p-10">
          <h2 className="text-2xl font-extrabold">{labels.nextStepTitle}</h2>
          <p className="mt-3 max-w-2xl text-night-muted">{labels.nextStepText}</p>
          <Link
            to={item.cta.to}
            className="mt-6 inline-flex min-h-12 items-center gap-2 rounded-md bg-night-foreground px-5 py-3 font-bold text-primary"
          >
            {item.cta.label}
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </div>

        {related.length > 0 && (
          <section className="mt-16">
            <h2 className="text-2xl font-bold">{labels.moreHeading}</h2>
            <ul className="mt-6 grid gap-6 md:grid-cols-3">
              {related.map((other) => (
                <li key={other.slug} className="rounded-lg border border-border bg-card p-5 shadow-card transition-colors hover:border-primary/60">
                  <h3 className="text-lg font-bold leading-snug">
                    <a
                      href={`${labels.sectionPath}/${other.slug}`}
                      className="underline-offset-4 hover:underline"
                    >
                      {other.title}
                    </a>
                  </h3>
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </>
  );
}
